import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Hash, Check, Loader2 } from "lucide-react";
import { firmApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";

const inputClasses =
  "w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm";

const labelClasses = "block text-sm font-medium text-slate-700 mb-1.5";

const DocumentNumberingPage: React.FC = () => {
  const navigate = useNavigate();
  const { firmData, refreshUserData } = useUserDataContext();

  const numbering = (firmData as any)?.settings?.numbering;

  const [matterPrefix, setMatterPrefix] = useState(numbering?.matterPrefix || "MAT");
  const [invoicePrefix, setInvoicePrefix] = useState(numbering?.invoicePrefix || "INV");
  const [receiptPrefix, setReceiptPrefix] = useState(numbering?.receiptPrefix || "TR");

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const n = (firmData as any)?.settings?.numbering;
    if (n) {
      setMatterPrefix(n.matterPrefix || "MAT");
      setInvoicePrefix(n.invoicePrefix || "INV");
      setReceiptPrefix(n.receiptPrefix || "TR");
    }
  }, [firmData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await firmApi.updateProfile({
        settings: {
          numbering: {
            matterPrefix: matterPrefix.trim() || "MAT",
            invoicePrefix: invoicePrefix.trim() || "INV",
            receiptPrefix: receiptPrefix.trim() || "TR",
          },
        },
      });
      await refreshUserData();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Failed to save numbering settings:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Document Numbering</h1>
            <p className="text-sm text-slate-500 mt-1">
              Customize the prefixes used for auto-generated reference numbers.
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white transition-all hover:shadow-md disabled:opacity-50"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                <Check className="h-4 w-4" />
                Saved
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>

      <div className="space-y-8 max-w-2xl">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Hash className="h-5 w-5" style={{ color: "var(--color-primary)" }} />
            <h3 className="text-base font-semibold text-slate-800">Reference Number Prefixes</h3>
          </div>

          <div className="space-y-6">
            {/* Matter Prefix */}
            <div>
              <label className={labelClasses}>Matter Prefix</label>
              <input
                type="text"
                value={matterPrefix}
                onChange={(e) => {
                  setMatterPrefix(e.target.value.toUpperCase());
                  setSaved(false);
                }}
                className={inputClasses}
                placeholder="MAT"
              />
              <p className="text-xs text-slate-400 mt-1.5">
                Example:{" "}
                <span className="font-mono text-slate-600">
                  {matterPrefix.trim() || "MAT"}-00001
                </span>
              </p>
            </div>

            {/* Invoice Prefix */}
            <div>
              <label className={labelClasses}>Invoice Prefix</label>
              <input
                type="text"
                value={invoicePrefix}
                onChange={(e) => {
                  setInvoicePrefix(e.target.value.toUpperCase());
                  setSaved(false);
                }}
                className={inputClasses}
                placeholder="INV"
              />
              <p className="text-xs text-slate-400 mt-1.5">
                Example:{" "}
                <span className="font-mono text-slate-600">
                  {invoicePrefix.trim() || "INV"}-00001
                </span>
              </p>
            </div>

            {/* Receipt Prefix */}
            <div>
              <label className={labelClasses}>Receipt Prefix</label>
              <input
                type="text"
                value={receiptPrefix}
                onChange={(e) => {
                  setReceiptPrefix(e.target.value.toUpperCase());
                  setSaved(false);
                }}
                className={inputClasses}
                placeholder="TR"
              />
              <p className="text-xs text-slate-400 mt-1.5">
                Example:{" "}
                <span className="font-mono text-slate-600">
                  {receiptPrefix.trim() || "TR"}-00001
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentNumberingPage;
